import { MutationFunction, useMutation } from '@apollo/client';

import { EventType } from 'types/event.types';
import { MultiSessionPlusOneGuest, PlusOneGuest } from 'types/plus-one.types';
import {
  ADMIN_REMOVE_PLUS_ONE_GUEST_MUTATION,
  AdminRemovePlusOneGuestResponse,
  AdminRemovePlusOneGuestVariables
} from 'graphql/mutations/rsvp_control/plus_one/admin-remove-plus-one-guest';
import {
  ADMIN_SET_SESSION_PLUS_ONE_GUESTS_MUTATION,
  AdminSetSessionPlusOneGuestsResponse,
  AdminSetSessionPlusOneGuestsVariables
} from 'graphql/mutations/rsvp_control/plus_one/admin-set-session-plus-one-guests';
import {
  ADMIN_UPDATE_PLUS_ONE_GUEST_MUTATION,
  AdminUpdatePlusOneGuestResponse,
  AdminUpdatePlusOneGuestVariables
} from 'graphql/mutations/rsvp_control/plus_one/admin-update-plus-one-guest';
import {
  GET_GUEST_DATA_FOR_RSVP_CONTROL_QUERY,
  GetGuestDataForRSVPControlQueryResponse,
  GetGuestDataForRSVPControlQueryVariables
} from 'graphql/queries/rsvp_control/get_guest_for_rsvp_control';

import { useGuestSideSheetContext } from '../../guestSideSheetContext';

interface UsePlusOneGuestMutationsReturn {
  savePlusOneGuest: MutationFunction<
    AdminUpdatePlusOneGuestResponse,
    AdminUpdatePlusOneGuestVariables
  >;
  setSessionPlusOneGuests: MutationFunction<
    AdminSetSessionPlusOneGuestsResponse,
    AdminSetSessionPlusOneGuestsVariables
  >;
  removePlusOneGuest: MutationFunction<
    AdminRemovePlusOneGuestResponse,
    AdminRemovePlusOneGuestVariables
  >;
  savingPlusOneGuest: boolean;
  removingPlusOneGuest: boolean;
  settingSessionPlusOneGuests: boolean;
}

export const usePlusOneGuestMutations = (): UsePlusOneGuestMutationsReturn => {
  const {
    selectedPlusOneGuest: plusOneGuest,
    eventOrSessionId: eventId,
    guestData: { guest, event }
  } = useGuestSideSheetContext();

  const { gid } = guest;
  const isEditMode = !!plusOneGuest?.plusOneId;
  const isMultiSessionEvent = event.eventType === EventType.MULTI_SESSIONS;

  const [savePlusOneGuest, { loading: savingPlusOneGuest }] = useMutation<
    AdminUpdatePlusOneGuestResponse,
    AdminUpdatePlusOneGuestVariables
  >(ADMIN_UPDATE_PLUS_ONE_GUEST_MUTATION, {
    update: (cache, { data }) => {
      if (!data?.adminUpdatePlusOneGuest.ok) return;

      const updatedPlusOneGuest = data.adminUpdatePlusOneGuest.plusOneGuest;

      const existingData = cache.readQuery<
        GetGuestDataForRSVPControlQueryResponse,
        GetGuestDataForRSVPControlQueryVariables
      >({
        query: GET_GUEST_DATA_FOR_RSVP_CONTROL_QUERY,
        variables: { gid, eventId }
      });

      if (!existingData) return;

      const currentPlusOnes = existingData.guestDataForRsvpControl.guest.plusOnes || [];

      const updatedPlusOnes = isEditMode
        ? (currentPlusOnes as PlusOneGuest[]).map((p) =>
            p.plusOneId === updatedPlusOneGuest.plusOneId ? updatedPlusOneGuest : p
          )
        : [...currentPlusOnes, updatedPlusOneGuest];

      // Update parentPlusOnes for multi-session events
      const existingParentPlusOnes = existingData.guestDataForRsvpControl.parentPlusOnes || [];
      let updatedParentPlusOnes: MultiSessionPlusOneGuest[];
      updatedParentPlusOnes = [];

      if (isMultiSessionEvent) {
        if (isEditMode) {
          // Update existing plus-one's fields (name, email, questionnaire) in parentPlusOnes
          updatedParentPlusOnes = existingParentPlusOnes.map((p) =>
            p.plusOneId === updatedPlusOneGuest.plusOneId
              ? {
                  ...p,
                  firstName: updatedPlusOneGuest.firstName,
                  lastName: updatedPlusOneGuest.lastName,
                  questionnaireResponses: updatedPlusOneGuest.questionnaireResponses
                }
              : p
          );
        } else {
          // Add new plus-one to parentPlusOnes as MultiSessionPlusOneGuest
          const newParentPlusOne: MultiSessionPlusOneGuest = {
            plusOneId: updatedPlusOneGuest.plusOneId,
            eventId: updatedPlusOneGuest.eventId,
            firstName: updatedPlusOneGuest.firstName,
            lastName: updatedPlusOneGuest.lastName,
            questionnaireResponses: updatedPlusOneGuest.questionnaireResponses,
            sessionStatus: [
              {
                eventId,
                status: updatedPlusOneGuest.status
              }
            ]
          };
          updatedParentPlusOnes = [...existingParentPlusOnes, newParentPlusOne];
        }
      }

      cache.writeQuery<
        GetGuestDataForRSVPControlQueryResponse,
        GetGuestDataForRSVPControlQueryVariables
      >({
        query: GET_GUEST_DATA_FOR_RSVP_CONTROL_QUERY,
        variables: { gid, eventId },
        data: {
          ...existingData,
          guestDataForRsvpControl: {
            ...existingData.guestDataForRsvpControl,
            guest: {
              ...existingData.guestDataForRsvpControl.guest,
              plusOnes: updatedPlusOnes
            },
            ...(updatedParentPlusOnes && {
              parentPlusOnes: updatedParentPlusOnes
            })
          }
        }
      });
    }
  });

  const [setSessionPlusOneGuests, { loading: settingSessionPlusOneGuests }] = useMutation<
    AdminSetSessionPlusOneGuestsResponse,
    AdminSetSessionPlusOneGuestsVariables
  >(ADMIN_SET_SESSION_PLUS_ONE_GUESTS_MUTATION, {
    update: (cache, { data }) => {
      if (!data?.adminSetSessionPlusOneGuests.ok) return;

      const { guest: updatedGuest } = data.adminSetSessionPlusOneGuests;

      const existingData = cache.readQuery<
        GetGuestDataForRSVPControlQueryResponse,
        GetGuestDataForRSVPControlQueryVariables
      >({
        query: GET_GUEST_DATA_FOR_RSVP_CONTROL_QUERY,
        variables: { gid, eventId }
      });

      if (!existingData) return;

      // Build a map of plusOneId -> status from the mutation result
      const updatedStatusMap = new Map(updatedGuest.plusOnes.map((p) => [p.plusOneId, p.status]));

      // Update parentPlusOnes sessionStatus for the current session
      const existingParentPlusOnes = existingData.guestDataForRsvpControl.parentPlusOnes;
      const updatedParentPlusOnes = existingParentPlusOnes
        ? existingParentPlusOnes.map((p) => {
            const newStatus = updatedStatusMap.get(p.plusOneId);

            if (newStatus === undefined) {
              // Plus-one was removed from this session — drop the session entry
              return {
                ...p,
                sessionStatus: p.sessionStatus.filter((s) => s.eventId !== eventId)
              };
            }

            const hasSession = p.sessionStatus.some((s) => s.eventId === eventId);
            return {
              ...p,
              sessionStatus: hasSession
                ? p.sessionStatus.map((s) =>
                    s.eventId === eventId ? { ...s, status: newStatus } : s
                  )
                : [...p.sessionStatus, { eventId, status: newStatus }]
            };
          })
        : undefined;

      cache.writeQuery<
        GetGuestDataForRSVPControlQueryResponse,
        GetGuestDataForRSVPControlQueryVariables
      >({
        query: GET_GUEST_DATA_FOR_RSVP_CONTROL_QUERY,
        variables: { gid, eventId },
        data: {
          ...existingData,
          guestDataForRsvpControl: {
            ...existingData.guestDataForRsvpControl,
            guest: {
              ...existingData.guestDataForRsvpControl.guest,
              plusOnes: updatedGuest.plusOnes
            },
            ...(updatedParentPlusOnes && {
              parentPlusOnes: updatedParentPlusOnes
            })
          }
        }
      });
    }
  });

  const [removePlusOneGuest, { loading: removingPlusOneGuest }] = useMutation<
    AdminRemovePlusOneGuestResponse,
    AdminRemovePlusOneGuestVariables
  >(ADMIN_REMOVE_PLUS_ONE_GUEST_MUTATION, {
    update: (cache, { data }) => {
      if (!data?.adminRemovePlusOneGuest.ok) return;

      const existingData = cache.readQuery<
        GetGuestDataForRSVPControlQueryResponse,
        GetGuestDataForRSVPControlQueryVariables
      >({
        query: GET_GUEST_DATA_FOR_RSVP_CONTROL_QUERY,
        variables: { gid, eventId }
      });

      if (!existingData) return;

      // Update parentPlusOnes for multi-session events
      const existingParentPlusOnes = existingData.guestDataForRsvpControl.parentPlusOnes;
      const updatedParentPlusOnes =
        isMultiSessionEvent && existingParentPlusOnes
          ? existingParentPlusOnes.filter((p) => p.plusOneId !== plusOneGuest?.plusOneId)
          : undefined;

      cache.writeQuery<
        GetGuestDataForRSVPControlQueryResponse,
        GetGuestDataForRSVPControlQueryVariables
      >({
        query: GET_GUEST_DATA_FOR_RSVP_CONTROL_QUERY,
        variables: { gid, eventId },
        data: {
          ...existingData,
          guestDataForRsvpControl: {
            ...existingData.guestDataForRsvpControl,
            guest: {
              ...existingData.guestDataForRsvpControl.guest,
              plusOnes: (
                existingData.guestDataForRsvpControl.guest.plusOnes as PlusOneGuest[]
              )?.filter((p) => p.plusOneId !== plusOneGuest?.plusOneId)
            },
            ...(updatedParentPlusOnes && {
              parentPlusOnes: updatedParentPlusOnes
            })
          }
        }
      });
    }
  });

  return {
    savePlusOneGuest,
    setSessionPlusOneGuests,
    removePlusOneGuest,
    savingPlusOneGuest,
    removingPlusOneGuest,
    settingSessionPlusOneGuests
  };
};
